/**
 * Dev-only candidate-line overlay.
 *
 * The page-9 vendor vector carries a number of thin stroke primitives —
 * divider rules, leader marks and other drawing furniture — that are NOT
 * engraved on the real chassis and should be suppressed. Deciding WHICH
 * are junk and which are genuine silkscreen (the AUTO SETUP bracket, the
 * WHITE / BLACK group frames) needs the operator's eye against the
 * reference photograph, so this overlay surfaces every candidate live in
 * the panel itself: each elongated unbound stroke is highlighted in
 * magenta and tagged with a number in the left gutter.
 *
 * It never runs in production. `firstUpdated` only calls it when the URL
 * carries a `candidates` flag (`…/rcp-3500?candidates` or
 * `#/panels/sony/rcp-3500?candidates`), so the panel renders clean by
 * default. The operator reads off the numbers of the junk lines; those
 * primitive ids are then pinned into IGNORED_PRIMITIVE_IDS, which hides
 * them permanently and survives the manifest rebuild.
 *
 * The numbering is deterministic (top-to-bottom, left-to-right by
 * measured position) so the same flag always yields the same map.
 */

import { IGNORED_PRIMITIVE_IDS } from './svg-substrate.ts';

const SVG_NS = 'http://www.w3.org/2000/svg';

const IGNORED = new Set<string>(IGNORED_PRIMITIVE_IDS);

interface Box {
  x: number;
  y: number;
  w: number;
  h: number;
}

interface Candidate extends Box {
  id: string;
  el: SVGGraphicsElement;
}

/**
 * Measure an element's bounding box in the SVG's user (viewBox) space.
 * `getBBox` alone is element-local — for primitives placed by nested
 * `<g transform>` matrices it returns the wrong rectangle — so we map
 * the local box corners through `svgInverse × element screen CTM`.
 */
function userBox(svgInverse: DOMMatrix, el: SVGGraphicsElement): Box {
  const b = el.getBBox();
  const screen = el.getScreenCTM();
  if (!screen) return { x: 0, y: 0, w: 0, h: 0 };
  const m = svgInverse.multiply(screen);
  const xs: number[] = [];
  const ys: number[] = [];
  for (const [px, py] of [
    [b.x, b.y],
    [b.x + b.width, b.y],
    [b.x, b.y + b.height],
    [b.x + b.width, b.y + b.height],
  ] as const) {
    const pt = new DOMPoint(px, py).matrixTransform(m);
    xs.push(pt.x);
    ys.push(pt.y);
  }
  const x = Math.min(...xs);
  const y = Math.min(...ys);
  return { x, y, w: Math.max(...xs) - x, h: Math.max(...ys) - y };
}

/**
 * True when the URL opts into the dev picker: `?debug=on` (anywhere in the
 * query or hash). `?debug=off` or no flag keeps the panel clean. `candidates`
 * stays a legacy alias.
 */
export function candidateOverlayRequested(): boolean {
  const { hash, search } = globalThis.location;
  const url = `${hash} ${search}`;
  return url.includes('debug=on') || url.includes('candidates');
}

/**
 * Highlight every elongated unbound stroke and number it. Returns the
 * number → primitive-id map (also logged) so the operator and the
 * maintainer share one vocabulary for "remove 3, 6".
 */
export function drawCandidateOverlay(
  svg: SVGSVGElement,
): readonly { n: number; id: string; box: Box }[] {
  const screen = svg.getScreenCTM();
  if (!screen) return [];
  const svgInverse = screen.inverse();

  const candidates: (Candidate & { hidden: boolean })[] = [];
  for (const el of svg.querySelectorAll<SVGGraphicsElement>('[data-prim-id]')) {
    if (el.hasAttribute('data-component-id')) continue; // a bound control, not furniture
    if (el.tagName.toLowerCase() === 'text') continue; // live label overlay
    const id = el.getAttribute('data-prim-id') ?? '';
    // Two kinds of display:none exist on the substrate — lines suppressed
    // by IGNORED_PRIMITIVE_IDS (the junk we cut) and vendor glyph outlines
    // that renderDerivedLabels replaced with live text. Only the former are
    // candidates: un-hide them so the operator sees what was removed (cyan).
    // Glyph outlines stay hidden and skipped — re-showing them would flood
    // the panel with traced letterforms.
    const hidden = IGNORED.has(id);
    if (el.style.display === 'none' && !hidden) continue;
    if (hidden) el.style.display = '';
    let box: Box;
    try {
      box = userBox(svgInverse, el);
    } catch {
      if (hidden) el.style.display = 'none';
      continue;
    }
    const min = Math.min(box.w, box.h);
    const max = Math.max(box.w, box.h);
    const aspect = min <= 0 ? Infinity : max / min;
    if (max < 6 || aspect < 3.5) {
      // Not a line (glyph / frame / ring / filled area). Re-hide if it was.
      if (hidden) el.style.display = 'none';
      continue;
    }
    candidates.push({ id, el, hidden, ...box });
  }

  candidates.sort((a, b) => a.y - b.y || a.x - b.x);

  const viewBox = svg.viewBox.baseVal;
  const map: { n: number; id: string; hidden: boolean; box: Box }[] = [];
  candidates.forEach((c, i) => {
    const n = i + 1;
    // Visible junk candidate → magenta. Already-removed line → cyan, so the
    // operator can tell "this is still here" from "this is what you cut".
    const colour = c.hidden ? '#10d0ff' : '#ff10c0';
    // setProperty(..., 'important') is REQUIRED: the substrate engrave rule
    // pins stroke with !important, and a plain inline style loses to it —
    // which is why the lines stayed un-coloured before.
    c.el.style.setProperty('stroke', colour, 'important');
    c.el.style.setProperty('stroke-width', '2.5', 'important');
    c.el.style.setProperty('opacity', '1', 'important');

    const cy = c.y + c.h / 2;
    const cx = Math.max(viewBox.x + 5, c.x - 7);

    const disc = document.createElementNS(SVG_NS, 'circle');
    disc.setAttribute('cx', String(cx));
    disc.setAttribute('cy', String(cy));
    disc.setAttribute('r', '5');
    disc.setAttribute('fill', colour);
    disc.setAttribute('stroke', '#000');
    disc.setAttribute('stroke-width', '0.8');
    disc.dataset['candidateOverlay'] = '';
    svg.appendChild(disc);

    const leader = document.createElementNS(SVG_NS, 'line');
    leader.setAttribute('x1', String(cx + 5));
    leader.setAttribute('y1', String(cy));
    leader.setAttribute('x2', String(c.x));
    leader.setAttribute('y2', String(cy));
    leader.setAttribute('stroke', colour);
    leader.setAttribute('stroke-width', '0.6');
    leader.dataset['candidateOverlay'] = '';
    svg.appendChild(leader);

    const text = document.createElementNS(SVG_NS, 'text');
    text.setAttribute('x', String(cx));
    text.setAttribute('y', String(cy));
    text.setAttribute('text-anchor', 'middle');
    text.setAttribute('dominant-baseline', 'central');
    text.setAttribute('font-family', 'monospace');
    text.setAttribute('font-size', '6');
    text.setAttribute('font-weight', '700');
    text.setAttribute('fill', '#000');
    text.textContent = String(n);
    text.dataset['candidateOverlay'] = '';
    svg.appendChild(text);

    map.push({ n, id: c.id, hidden: c.hidden, box: { x: c.x, y: c.y, w: c.w, h: c.h } });
  });

  // Surfaced to the console so the maintainer can read the same map the
  // operator sees on screen. magenta = currently shown, cyan = already cut.
  console.info('[rcp-3500] candidate lines (magenta=shown, cyan=removed):', map);
  return map;
}

/**
 * Click-to-pick selection — name the part under the cursor and copy its id.
 *
 * The hit test is the platform's own (shadow-root elementsFromPoint), so a
 * click anywhere on a part's BODY selects it, not merely within a few units of
 * its bounding-box edge: filled dial caps, the chassis panes, the bezel and the
 * glass all pick reliably. Repeated clicks at the SAME point walk down the
 * z-stack (cap → ring → pointer → the pane behind), so every overlapping part
 * of a knob is reachable. A hair-thin unbound stroke the cursor narrowly misses
 * is still caught by an edge-distance fallback. Picked parts get a non-scaling
 * red outline (their fill is untouched) and a row with the id, a name field, a
 * per-row copy and a remove; the footer copies every id — or every id: name
 * mapping — to the clipboard. Dev-only; never wired in production.
 */
export function enableInteractiveSelect(svg: SVGSVGElement): void {
  const screen = svg.getScreenCTM();
  if (!screen) return;
  const svgInverse = screen.inverse();

  // Un-hide the already-suppressed (IGNORED) primitives so the operator can
  // re-mark even features that were previously removed. They are tinted faint
  // cyan so "currently removed" reads differently from "currently shown", and
  // become hittable like everything else.
  for (const id of IGNORED) {
    const el = svg.querySelector<SVGElement>(`[data-prim-id="${id}"]`);
    if (el?.style.display !== 'none') continue;
    el.style.display = '';
    el.style.setProperty('stroke', '#10d0ff', 'important');
    el.style.setProperty('stroke-width', '1.4', 'important');
    el.style.setProperty('opacity', '0.85', 'important');
  }

  // The id reported for a pickable element. Bound controls report
  // `component-id (prim-id)`; unbound furniture its prim-id; the re-drawn
  // background panes their source-primitive reference; a live silkscreen label
  // its text plus rounded position (so the twin RELATIVE keys stay distinct).
  // Anything else under the cursor is not a pick target.
  const pickId = (el: Element): string | null => {
    if (!(el instanceof SVGGraphicsElement)) return null;
    if (el.dataset['candidateOverlay'] !== undefined) return null;
    const primId = el.getAttribute('data-prim-id');
    if (primId) {
      const compId = el.getAttribute('data-component-id');
      return compId ? `${compId} (${primId})` : primId;
    }
    if (el.dataset['modulePane']) return `${el.dataset['modulePane']} (module-pane)`;
    if (el.dataset['connectedPane']) return `${el.dataset['connectedPane']} (connected-pane)`;
    if (el.dataset['labelOverlay'] !== undefined && el.tagName.toLowerCase() === 'text') {
      let lbox: Box;
      try {
        lbox = userBox(svgInverse, el);
      } catch {
        return null;
      }
      const label = el.textContent.trim();
      return `label:"${label}"@${String(Math.round(lbox.x))},${String(Math.round(lbox.y))}`;
    }
    return null;
  };

  // Edge-distance fallback list — every pickable element's box in user space.
  // Used only when the platform hit-test finds nothing under the cursor (a
  // hair-thin unbound stroke the click narrowly missed). The re-drawn panes and
  // live labels are included so the fallback can reach them too.
  interface Item {
    el: SVGGraphicsElement;
    id: string;
    box: Box;
  }
  const items: Item[] = [];
  const PICK_SEL =
    '[data-prim-id], [data-module-pane], [data-connected-pane], text[data-label-overlay]';
  for (const el of svg.querySelectorAll<SVGGraphicsElement>(PICK_SEL)) {
    const id = pickId(el);
    if (id === null) continue;
    let box: Box;
    try {
      box = userBox(svgInverse, el);
    } catch {
      continue;
    }
    if (box.w <= 0 && box.h <= 0) continue;
    items.push({ el, id, box });
  }

  // Naming GUI: click a part → it gets a red outline and a row carrying its id,
  // a name field, a per-row copy (⧉) and a remove (✕). The footer copies every
  // id, or every `id: name` mapping, straight to the clipboard (the green <pre>
  // is the select-all fallback when the clipboard API is unavailable). Appended
  // at document level so it shows over the panel across shadow boundaries, and
  // re-created cleanly across HMR reloads.
  const rows = new Map<
    string,
    { row: HTMLDivElement; input: HTMLInputElement; restore: () => void }
  >();

  const existing = document.getElementById('rcp-hide-list');
  if (existing) existing.remove();
  const readout = document.createElement('div');
  readout.id = 'rcp-hide-list';
  readout.style.cssText =
    'position:fixed;top:0.5rem;left:0.5rem;z-index:99999;background:rgba(0,0,0,0.9);' +
    'color:#e8e8e8;font:0.72rem/1.4 monospace;padding:0.6rem 0.7rem;border:1px solid #ff6a00;' +
    'border-radius:0.4rem;max-width:24rem;max-height:90vh;overflow:auto;pointer-events:auto;';
  const head = document.createElement('div');
  head.style.cssText = 'color:#ff9a3a;margin-bottom:0.4rem;';
  head.textContent =
    'KLICKA en del → den får en rad med sitt id. Klicka igen på samma punkt för att borra ned till delen under.';
  const list = document.createElement('div');
  const out = document.createElement('pre');
  out.style.cssText = 'margin:0.5rem 0 0;white-space:pre-wrap;user-select:all;color:#7dff8a;';

  const btnCss =
    'font:inherit;cursor:pointer;background:#ff6a00;color:#000;border:0;' +
    'border-radius:0.25rem;padding:0.25rem 0.55rem;';
  const miniBtnCss =
    'font:inherit;cursor:pointer;background:#333;color:#eee;border:1px solid #555;' +
    'border-radius:0.2rem;padding:0.05rem 0.35rem;flex:0 0 auto;';

  // Write to the clipboard and flash the button; the <pre> mirrors the text as a
  // select-all fallback (navigator.clipboard needs a secure context).
  const writeClipboard = (text: string, button: HTMLButtonElement, ok: string): void => {
    out.textContent = text || '—';
    const label = button.textContent;
    void navigator.clipboard.writeText(text).then(
      () => {
        button.textContent = ok;
        setTimeout(() => {
          button.textContent = label;
        }, 1000);
      },
      () => {
        /* the <pre> is the fallback */
      },
    );
  };

  const footer = document.createElement('div');
  footer.style.cssText = 'display:flex;gap:0.4rem;margin-top:0.5rem;';
  const copyIdsBtn = document.createElement('button');
  copyIdsBtn.textContent = 'Kopiera alla id';
  copyIdsBtn.style.cssText = btnCss;
  copyIdsBtn.addEventListener('click', () => {
    writeClipboard([...rows.keys()].join('\n'), copyIdsBtn, 'Kopierat ✓');
  });
  const copyMapBtn = document.createElement('button');
  copyMapBtn.textContent = 'Kopiera mappning';
  copyMapBtn.style.cssText = btnCss;
  copyMapBtn.addEventListener('click', () => {
    const lines = [...rows].map(([id, { input }]) => `${id}: ${input.value.trim() || '?'}`);
    writeClipboard(lines.join('\n'), copyMapBtn, 'Kopierat ✓');
  });
  footer.append(copyIdsBtn, copyMapBtn);
  readout.append(head, list, footer, out);
  document.body.appendChild(readout);

  // Properties the red selection outline overrides. Snapshot them per element so
  // a part's substrate styling (its inline !important stroke / fill) is restored
  // EXACTLY on removal — these are carefully-painted physical parts, not junk.
  const HILITE = ['stroke', 'stroke-width', 'vector-effect', 'fill', 'opacity'] as const;
  const highlight = (el: SVGGraphicsElement): (() => void) => {
    const saved = HILITE.map(
      (p) => [p, el.style.getPropertyValue(p), el.style.getPropertyPriority(p)] as const,
    );
    if (el.tagName.toLowerCase() === 'text') {
      // Glyphs are filled, not stroked — a red fill is what actually shows.
      el.style.setProperty('fill', '#ff3030', 'important');
    } else {
      // A non-scaling red OUTLINE marks the part at any group scale WITHOUT
      // painting over its fill, so a selected cap keeps its channel colour.
      el.style.setProperty('stroke', '#ff3030', 'important');
      el.style.setProperty('stroke-width', '2', 'important');
      el.style.setProperty('vector-effect', 'non-scaling-stroke', 'important');
    }
    el.style.setProperty('opacity', '1', 'important');
    return () => {
      for (const [p, value, priority] of saved) {
        if (value) el.style.setProperty(p, value, priority);
        else el.style.removeProperty(p);
      }
    };
  };

  const removeRow = (id: string): void => {
    const r = rows.get(id);
    if (!r) return;
    r.restore();
    r.row.remove();
    rows.delete(id);
  };
  const addRow = (id: string, el: SVGGraphicsElement): void => {
    const present = rows.get(id);
    if (present) {
      // preventScroll: focusing the name field must not yank a pinch-/browser-
      // zoomed viewport back to the fixed panel in the top-left corner, which
      // would throw the operator out of their zoom on every pick.
      present.input.focus({ preventScroll: true });
      return;
    }
    const restore = highlight(el);
    const row = document.createElement('div');
    row.style.cssText = 'display:flex;gap:0.3rem;align-items:center;margin:0.15rem 0;';
    const tag = document.createElement('code');
    tag.textContent = id;
    tag.title = id;
    tag.style.cssText =
      'color:#ff8a8a;flex:0 0 auto;max-width:9rem;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;';
    const input = document.createElement('input');
    input.placeholder = 'vad är det?';
    input.style.cssText =
      'flex:1 1 auto;min-width:4rem;font:inherit;background:#111;color:#fff;border:1px solid #555;' +
      'border-radius:0.2rem;padding:0.1rem 0.3rem;';
    const copyOne = document.createElement('button');
    copyOne.textContent = '⧉';
    copyOne.title = 'Kopiera id';
    copyOne.style.cssText = miniBtnCss;
    copyOne.addEventListener('click', () => {
      writeClipboard(id, copyOne, '✓');
    });
    const del = document.createElement('button');
    del.textContent = '✕';
    del.title = 'Ta bort';
    del.style.cssText = miniBtnCss;
    del.addEventListener('click', () => {
      removeRow(id);
    });
    row.append(tag, input, copyOne, del);
    list.appendChild(row);
    rows.set(id, { row, input, restore });
    // preventScroll: see the matching note above — keep the operator's zoom.
    input.focus({ preventScroll: true });
  };

  // Distance from a point to a box's nearest EDGE (0 on the edge, grows
  // inward and outward). Selecting by edge proximity — not by "smallest
  // box that contains the point" — is what makes a large frame pickable:
  // clicking on the frame's outline gives it distance ~0, whereas the old
  // containment test always handed the click to some smaller primitive
  // sitting inside the frame (e.g. the LCD well inside its surround).
  const edgeDist = (px: number, py: number, b: Box): number => {
    const insideX = px >= b.x && px <= b.x + b.w;
    const insideY = py >= b.y && py <= b.y + b.h;
    if (insideX && insideY) {
      return Math.min(px - b.x, b.x + b.w - px, py - b.y, b.y + b.h - py);
    }
    const dx = Math.max(b.x - px, px - (b.x + b.w), 0);
    const dy = Math.max(b.y - py, py - (b.y + b.h), 0);
    return Math.hypot(dx, dy);
  };

  let lastClient: { x: number; y: number } | null = null;

  svg.addEventListener('click', (ev: MouseEvent) => {
    const here = svg.getScreenCTM();
    if (!here) return;

    // Build the pickable stack at the click point from three sources, then
    // order it by ascending bounding-box AREA so the most specific part comes
    // first. Area-first is what makes a DIAL pickable: its cap and rings are
    // unfilled circles, so the platform hit-test passes straight through their
    // hollow centre to the pane behind — yet their (small) boxes still contain
    // the click and beat that big pane on area.
    //   • painted   — the shadow root's own elementsFromPoint (it sees through
    //     the boundary document.elementsFromPoint cannot); pixel-accurate for
    //     filled parts (caps, panes, glass, the bezel).
    //   • contained — any pickable box covering the click; catches the hollow
    //     centre of a ring or cap the painted test misses.
    //   • near      — a hair-thin unbound stroke the cursor narrowly missed.
    const root = svg.getRootNode() as Document | ShadowRoot;
    const painted = new Set<Element>(root.elementsFromPoint(ev.clientX, ev.clientY));
    const pt = new DOMPoint(ev.clientX, ev.clientY).matrixTransform(here.inverse());
    const margin = 4;
    const stack = items
      .filter((it) => {
        if (painted.has(it.el)) return true;
        const b = it.box;
        const inside = pt.x >= b.x && pt.x <= b.x + b.w && pt.y >= b.y && pt.y <= b.y + b.h;
        return inside || edgeDist(pt.x, pt.y, it.box) <= margin;
      })
      .sort((a, b) => a.box.w * a.box.h - b.box.w * b.box.h);

    const top = stack[0];
    const repeat =
      lastClient !== null && Math.hypot(ev.clientX - lastClient.x, ev.clientY - lastClient.y) < 8;
    lastClient = { x: ev.clientX, y: ev.clientY };
    if (!top) return;

    if (repeat) {
      // Drill: select the first part in the stack not already picked, so each
      // same-point click reaches one layer deeper (cap → ring → pointer → pane).
      const next = stack.find((s) => !rows.has(s.id));
      if (next) addRow(next.id, next.el);
      return;
    }
    // Fresh point: toggle the topmost part — off if it was already picked.
    if (rows.has(top.id)) removeRow(top.id);
    else addRow(top.id, top.el);
  });
}
